import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiCircle, FiExternalLink, FiEye, FiSliders } = FiIcons;

const ChecklistPreview = ({ checklist, darkMode }) => {
  const [completedItems, setCompletedItems] = useState(new Set());
  const [isFloatingMode, setIsFloatingMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customization, setCustomization] = useState({
    buttonText: "✓ Marcos",
    buttonPosition: "bottom-right",
    buttonGradientStart: "#667eea",
    buttonGradientEnd: "#764ba2",
    activeButtonGradientStart: "#f87171",
    activeButtonGradientEnd: "#ef4444",
    buttonSize: "medium",
    buttonBorderRadius: "full",
    buttonShadow: "medium",
    showProgressInButton: true,
    showProgressBar: true,
    progressBarColor: "#10b981",
    // Checklist customization
    showTitle: true,
    showDescription: true,
    itemBackgroundColor: "#ffffff",
    itemBorderColor: "#e5e7eb",
    itemHoverColor: "#f3f4f6",
    completedItemColor: "#f0fdf4",
    completedBorderColor: "#10b981",
    checkboxColor: "#10b981",
    textColor: "#374151",
    linkColor: "#3b82f6",
    modalBackgroundColor: "#ffffff",
    modalBorderRadius: "16px",
    itemBorderRadius: "12px"
  });

  const toggleItem = (itemId) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(itemId)) {
      newCompleted.delete(itemId);
    } else {
      newCompleted.add(itemId);
    }
    setCompletedItems(newCompleted);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleCustomizationChange = (field, value) => {
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const getProgress = () => {
    const totalItems = checklist.items.length;
    const completedCount = completedItems.size;
    const percentage = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
    return { completed: completedCount, total: totalItems, percentage };
  };

  const progress = getProgress();

  // Get position classes for the button
  const getButtonPosition = () => {
    switch(customization.buttonPosition) {
      case 'bottom-right': return 'bottom-8 right-8';
      case 'bottom-left': return 'bottom-8 left-8';
      case 'top-right': return 'top-8 right-8';
      case 'top-left': return 'top-8 left-8';
      case 'middle-right': return 'top-1/2 -translate-y-1/2 right-8';
      case 'middle-left': return 'top-1/2 -translate-y-1/2 left-8';
      default: return 'bottom-8 right-8';
    }
  };

  // Get button size
  const getButtonSize = () => {
    switch(customization.buttonSize) {
      case 'small': return 'min-w-[45px] h-[45px] text-sm';
      case 'medium': return 'min-w-[60px] h-[60px] text-base';
      case 'large': return 'min-w-[75px] h-[75px] text-lg';
      default: return 'min-w-[60px] h-[60px] text-base';
    }
  };

  // Get button border radius
  const getButtonBorderRadius = () => {
    switch(customization.buttonBorderRadius) {
      case 'none': return 'rounded-none';
      case 'small': return 'rounded-md';
      case 'medium': return 'rounded-xl';
      case 'large': return 'rounded-3xl';
      case 'full': return 'rounded-full';
      default: return 'rounded-full';
    }
  };

  // Get button shadow
  const getButtonShadow = () => {
    switch(customization.buttonShadow) {
      case 'none': return '';
      case 'small': return 'shadow-sm';
      case 'medium': return 'shadow-lg';
      case 'large': return 'shadow-xl';
      default: return 'shadow-lg';
    }
  };

  return (
    <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center space-x-2">
            <SafeIcon icon={FiEye} />
            <span>Preview da Lista</span>
          </h3>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFloatingMode(false)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                !isFloatingMode 
                  ? 'bg-blue-600 text-white' 
                  : darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Normal
            </button>
            <button
              onClick={() => setIsFloatingMode(true)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                isFloatingMode 
                  ? 'bg-blue-600 text-white' 
                  : darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Flutuante
            </button>
          </div>
        </div>

        {!isFloatingMode ? (
          // Preview Normal
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <div className="mb-4">
              <h4 className="text-lg font-bold mb-2">{checklist.title}</h4>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {checklist.description}
              </p>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">
                    Progresso: {progress.completed} de {progress.total} marcos
                  </span>
                  <span className="text-sm font-medium">
                    {progress.percentage}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${progress.percentage}%`,
                      backgroundColor: customization.progressBarColor
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {checklist.items.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-start space-x-3 p-3 rounded-lg transition-all cursor-pointer ${
                    completedItems.has(item.id)
                      ? darkMode 
                        ? 'bg-green-900/20 border border-green-700' 
                        : 'bg-green-50 border border-green-200'
                      : darkMode 
                        ? 'bg-gray-600 hover:bg-gray-500' 
                        : 'bg-white hover:bg-gray-50 border border-gray-200'
                  }`}
                  onClick={() => toggleItem(item.id)}
                >
                  <SafeIcon 
                    icon={completedItems.has(item.id) ? FiCheckCircle : FiCircle} 
                    className={`text-xl mt-0.5 ${
                      completedItems.has(item.id) ? 'text-green-600' : 'text-gray-400'
                    }`} 
                  />
                  <div className="flex-1 flex flex-col gap-1">
                    <div className={`font-medium block w-full ${completedItems.has(item.id) ? 'line-through opacity-75' : ''}`}>
                      {item.title}
                    </div>
                    <div className={`text-sm block w-full ${darkMode ? 'text-gray-300' : 'text-gray-600'} ${completedItems.has(item.id) ? 'line-through opacity-75' : ''}`}>
                      {item.description}
                    </div>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-fit text-blue-600 hover:text-blue-700 text-sm mt-2 px-2 py-1 rounded bg-blue-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Acessar</span>
                        <SafeIcon icon={FiExternalLink} className="inline ml-1 text-xs" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Preview Flutuante
          <>
            {/* Customization Panel */}
            <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiSliders} className="mr-2" />
                <h4 className="font-bold">Personalizar Preview</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Botão e Modal */}
                <div className="space-y-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Texto do Botão
                    </label>
                    <input
                      type="text"
                      value={customization.buttonText}
                      onChange={(e) => handleCustomizationChange('buttonText', e.target.value)}
                      className={`w-full px-3 py-2 rounded border text-sm ${
                        darkMode 
                          ? 'bg-gray-800 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Curvatura das Bordas
                    </label>
                    <select
                      value={customization.buttonBorderRadius}
                      onChange={(e) => handleCustomizationChange('buttonBorderRadius', e.target.value)}
                      className={`w-full px-3 py-2 rounded border text-sm ${
                        darkMode 
                          ? 'bg-gray-800 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="none">Sem Curvatura</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                      <option value="full">Totalmente Arredondado</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={customization.showTitle}
                        onChange={(e) => handleCustomizationChange('showTitle', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Mostrar Título</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={customization.showDescription}
                        onChange={(e) => handleCustomizationChange('showDescription', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Mostrar Descrição</span>
                    </label>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={customization.showProgressInButton}
                        onChange={(e) => handleCustomizationChange('showProgressInButton', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Progresso no Botão</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={customization.showProgressBar}
                        onChange={(e) => handleCustomizationChange('showProgressBar', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Barra de Progresso</span>
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Cor Botão 1
                      </label>
                      <input
                        type="color"
                        value={customization.buttonGradientStart}
                        onChange={(e) => handleCustomizationChange('buttonGradientStart', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Cor Botão 2
                      </label>
                      <input
                        type="color"
                        value={customization.buttonGradientEnd}
                        onChange={(e) => handleCustomizationChange('buttonGradientEnd', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                {/* Cores dos Itens */}
                <div className="space-y-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Cor da Barra de Progresso
                    </label>
                    <input
                      type="color"
                      value={customization.progressBarColor}
                      onChange={(e) => handleCustomizationChange('progressBarColor', e.target.value)}
                      className="w-full h-8 rounded border cursor-pointer"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Fundo Item
                      </label>
                      <input
                        type="color"
                        value={customization.itemBackgroundColor}
                        onChange={(e) => handleCustomizationChange('itemBackgroundColor', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Item Concluído
                      </label>
                      <input
                        type="color"
                        value={customization.completedItemColor}
                        onChange={(e) => handleCustomizationChange('completedItemColor', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Checkbox
                      </label>
                      <input
                        type="color"
                        value={customization.checkboxColor}
                        onChange={(e) => handleCustomizationChange('checkboxColor', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Cor Texto
                      </label>
                      <input
                        type="color"
                        value={customization.textColor}
                        onChange={(e) => handleCustomizationChange('textColor', e.target.value)}
                        className="w-full h-8 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Fundo do Modal
                    </label>
                    <input
                      type="color"
                      value={customization.modalBackgroundColor}
                      onChange={(e) => handleCustomizationChange('modalBackgroundColor', e.target.value)}
                      className="w-full h-8 rounded border cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className={`p-4 rounded-lg border-2 border-dashed ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50/50'}`}>
                <div className="text-center py-8">
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Simulação do widget flutuante na sua página:
                  </p>
                  
                  <div 
                    className={`${getButtonSize()} ${getButtonBorderRadius()} ${getButtonShadow()} px-4 mx-auto flex flex-col items-center justify-center font-medium transition-all text-white gap-0.5`}
                    style={{
                      background: `linear-gradient(135deg, ${customization.buttonGradientStart}, ${customization.buttonGradientEnd})`
                    }}
                  >
                    <div>{customization.buttonText}</div>
                    {customization.showProgressInButton && (
                      <div className="text-xs opacity-80">
                        {progress.completed}/{progress.total}
                      </div>
                    )}
                  </div>
                  
                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Botão flutuante ({customization.buttonPosition.replace('-', ' ')})
                  </p>
                  
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Progresso no botão: {customization.showProgressInButton ? 'Ativado' : 'Desativado'}
                  </p>
                  
                  <button
                    onClick={toggleModal}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Testar Modal
                  </button>
                </div>
              </div>

              {/* Modal de Demonstração */}
              {isModalOpen && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-5"
                  onClick={toggleModal}
                >
                  <div
                    className="max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 relative"
                    style={{
                      backgroundColor: customization.modalBackgroundColor,
                      borderRadius: customization.modalBorderRadius
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={toggleModal}
                      className="absolute top-4 right-4 w-8 h-8 bg-black bg-opacity-10 rounded-full flex items-center justify-center hover:bg-opacity-20 transition-all z-10"
                      style={{ color: customization.textColor }}
                    >
                      ✕
                    </button>
                    
                    {/* Header (opcional) */}
                    {(customization.showTitle || customization.showDescription) && (
                      <div className="p-6 pb-4 border-b border-gray-200">
                        {customization.showTitle && (
                          <h3 className="text-xl font-bold mb-2 pr-10" style={{ color: customization.textColor }}>
                            {checklist.title}
                          </h3>
                        )}
                        {customization.showDescription && (
                          <p className="text-sm pr-10 leading-5" style={{ color: customization.textColor, opacity: 0.7 }}>
                            {checklist.description}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Progress Section */}
                    {customization.showProgressBar && (
                      <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex justify-between items-center mb-2 text-sm font-medium" style={{ color: customization.textColor }}>
                          <span>Progresso: {progress.completed} de {progress.total} marcos</span>
                          <span>{progress.percentage}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full transition-all duration-300 rounded-full"
                            style={{ 
                              width: `${progress.percentage}%`,
                              backgroundColor: customization.progressBarColor
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Items */}
                    <div className="p-6 max-h-96 overflow-y-auto space-y-3">
                      {checklist.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all hover:transform hover:-translate-y-0.5"
                          style={{
                            backgroundColor: completedItems.has(item.id) 
                              ? customization.completedItemColor 
                              : customization.itemBackgroundColor,
                            borderColor: completedItems.has(item.id) 
                              ? customization.completedBorderColor 
                              : customization.itemBorderColor,
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: customization.itemBorderRadius
                          }}
                          onClick={() => toggleItem(item.id)}
                        >
                          <div 
                            className="w-5.5 h-5.5 rounded-full border-2 flex items-center justify-center text-sm mt-0.5 transition-all text-white"
                            style={{ 
                              width: '22px', 
                              height: '22px',
                              backgroundColor: completedItems.has(item.id) ? customization.checkboxColor : 'transparent',
                              borderColor: completedItems.has(item.id) ? customization.checkboxColor : '#d1d5db'
                            }}
                          >
                            {completedItems.has(item.id) && '✓'}
                          </div>
                          <div className="flex-1 flex flex-col gap-1">
                            <div 
                              className={`font-semibold leading-5 block w-full ${completedItems.has(item.id) ? 'line-through opacity-70' : ''}`}
                              style={{ color: customization.textColor }}
                            >
                              {item.title}
                            </div>
                            <div 
                              className={`text-sm leading-5 block w-full ${completedItems.has(item.id) ? 'line-through opacity-50' : 'opacity-70'}`}
                              style={{ color: customization.textColor }}
                            >
                              {item.description}
                            </div>
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-fit px-3 py-1.5 rounded-md transition-all mt-1 font-medium"
                                style={{ 
                                  color: customization.linkColor,
                                  backgroundColor: `${customization.linkColor}20`
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Acessar ↗
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChecklistPreview;